import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ChatbotConfig {
  id: string;
  name_dk: string;
  name_en: string;
  title_dk: string;
  title_en: string;
  welcome_text_dk: string;
  welcome_text_en: string;
  first_message_dk: string;
  first_message_en: string;
  image_generation_enabled?: boolean;
  image_size?: string;
  text_model?: string;
  image_model?: string;
  image_quality?: string;
  selected_style_preset_id?: string | null;
  image_style_dk?: string | null;
  image_style_en?: string | null;
  max_images_per_session?: number;
  quick_reply_1_label_dk?: string;
  quick_reply_1_label_en?: string;
  quick_reply_1_prompt_dk?: string;
  quick_reply_1_prompt_en?: string;
  quick_reply_2_label_dk?: string;
  quick_reply_2_label_en?: string;
  quick_reply_2_prompt_dk?: string;
  quick_reply_2_prompt_en?: string;
  quick_reply_3_label_dk?: string;
  quick_reply_3_label_en?: string;
  quick_reply_3_prompt_dk?: string;
  quick_reply_3_prompt_en?: string;
  created_at: string;
  updated_at: string;
}

export const useChatbotConfig = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: config, isLoading } = useQuery({
    queryKey: ['chatbot-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('chatbot_config')
        .select('*')
        .single();

      if (error) throw error;
      return data as ChatbotConfig;
    },
  });

  const updateConfig = useMutation({
    mutationFn: async (updates: Partial<ChatbotConfig>) => {
      if (!config?.id) throw new Error('No config found');

      const { data, error } = await supabase
        .from('chatbot_config')
        .update(updates)
        .eq('id', config.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['chatbot-config'] });
      toast({
        title: 'Success',
        description: 'Chatbot configuration updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    config,
    isLoading,
    updateConfig: updateConfig.mutate,
    isUpdating: updateConfig.isPending,
  };
};
